import { createRoot } from 'react-dom/client';
import { memo } from 'react';
import './index.css';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';
import PageNotFound from './components/Page-not-found';
import { Spinner } from './components/ui/spinner';
import { AuthProvider, useAuth, type AuthContextType } from './context/AuthContext';
import { ThemeProvider, initTheme } from '@/components/theme-provider';
import { ToastContainer } from 'react-toastify';
import { useServiceWorker } from './hooks/pushweb-notifications/useServiceWorker';
import { usePushNotifications } from './hooks/pushweb-notifications/useScheduleNotifications';
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"

const queryClient = new QueryClient()

Sentry.init({
  dsn: "https://6db47fe504716be564130387a58586ef@o4511071703138305.ingest.us.sentry.io/4511071711723520",
  sendDefaultPii: true,
  environment: import.meta.env.MODE,
  tracesSampleRate: import.meta.env.PROD ? 1.0 : 0.0,
  beforeSend(event, hint) {
    // Bug conocido del SDK de Appwrite: el heartbeat de realtime dispara
    // ws.send() antes de que el WebSocket alcance estado OPEN, lanzando
    // InvalidStateError. El WS conecta igual — no se pierde ningún dato.
    const err = hint?.originalException
    if (
      err instanceof DOMException &&
      err.name === 'InvalidStateError' &&
      typeof err.message === 'string' &&
      err.message.toLowerCase().includes('websocket')
    ) {
      return null
    }
    return event
  },
});


initTheme('dark', 'vite-ui-theme');

// Auto-reload cuando falla la carga de un chunk tras un nuevo deploy
window.addEventListener('unhandledrejection', (event) => {
  const message = event.reason?.message ?? ''
  const isChunkError =
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('Unable to preload CSS')

  if (isChunkError) {
    const lastReload = sessionStorage.getItem('chunk-error-reload')
    const now = Date.now()
    if (!lastReload || now - parseInt(lastReload) > 15000) {
      sessionStorage.setItem('chunk-error-reload', String(now))
      window.location.reload()
    }
  }
})

const ChunkErrorFallback = ({ error, reset }: { error: Error; reset: () => void }) => {
  const isChunkError =
    error?.message?.includes('Failed to fetch dynamically imported module') ||
    error?.message?.includes('Importing a module script failed')

  if (isChunkError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 text-center px-4">
        <p className="text-lg font-medium">Nueva versión disponible</p>
        <p className="text-sm text-muted-foreground">La aplicación se actualizó. Recarga la página para continuar.</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium cursor-pointer"
        >
          Recargar
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-4 text-center px-4">
      <p className="text-lg font-medium">Algo salió mal</p>
      <p className="text-sm text-muted-foreground">Ocurrió un error inesperado.</p>
      <button
        onClick={reset}
        className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium cursor-pointer"
      >
        Intentar de nuevo
      </button>
    </div>
  )
}

// Configuración del router
const router = createRouter({
  routeTree,
  defaultNotFoundComponent: () => <PageNotFound />,
  defaultErrorComponent: ({ error, reset }) => <ChunkErrorFallback error={error as Error} reset={reset} />,
  defaultPendingComponent: () => <Spinner />,
  defaultPendingMs: 0,
  context: {
    auth: undefined!,
  },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
  interface RouterContext {
    auth: AuthContextType;
  }
}

const NotificationSetup = memo(() => {
  useServiceWorker();
  usePushNotifications();
  return null;
});

NotificationSetup.displayName = 'NotificationSetup';

const InnerApp = memo(() => {
  const auth = useAuth();
  return (
    <>
      <NotificationSetup />
      <RouterProvider router={router} context={{ auth }} />
    </>
  );
});

InnerApp.displayName = 'InnerApp';

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}



createRoot(rootElement).render(
  <Sentry.ErrorBoundary fallback={({ error, resetError }) => (
    <ChunkErrorFallback error={error as Error} reset={resetError} />
  )}>
  <><AuthProvider>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
        <div className="flex bg-gray-100 dark:bg-zinc-900 min-h-screen rounded-sm relative">

          {/* Grid modo claro */}
          <div
            className="fixed inset-0 pointer-events-none dark:hidden"
            style={{
              backgroundImage: `
        linear-gradient(rgba(0,0,0,0.06) 1px, transparent 1px),
        linear-gradient(90deg, rgba(0,0,0,0.06) 1px, transparent 1px)
      `,
              backgroundSize: '64px 64px',
              opacity: 0.5,
              zIndex: 0,
            }} />

          {/* Grid mdoo dark */}
          <div
            className="fixed inset-0 pointer-events-none hidden dark:block"
            style={{
              backgroundImage: `
        linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)
      `,
              backgroundSize: '64px 64px',
              opacity: 1,
              zIndex: 0,
            }} />

          <div className='pt-2 lg:pt-0 w-full' style={{ position: 'relative', zIndex: 1 }}>
            <ToastContainer theme='colored' position='top-right' />
            <InnerApp />
          </div>
        </div>
      </ThemeProvider>
    </QueryClientProvider>
  </AuthProvider>
  </>
  </Sentry.ErrorBoundary>
);
